import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import { 
  Category, 
  CategorySchema, 
  CategoryCreateSchema, 
  CategoryUpdateSchema, 
  CategoryColor, 
  createCategory, 
  updateCategory 
} from '@/api/api.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';

export default function CategoryForm({
  category,
  onClose,
  isEditing = true,
}: {
  category: Category;
  onClose: () => void;
  isEditing?: boolean;
}) {
  const form = useForm<Category>({
    resolver: zodResolver(isEditing ? CategorySchema : CategoryCreateSchema),
    defaultValues: {
      ...category,
      // Convert null values to empty strings to avoid React warnings
      labelDe: category.labelDe ?? '',
      descriptionDe: category.descriptionDe ?? '',
    },
  });

  const onSubmit = useCallback(
    async (data: Category) => {
      try {
        // Helper function to convert empty strings to null for submission
        const emptyToNull = (value: string | null | undefined) => 
          value && value.trim() !== '' ? value : null;

        const categoryData = {
          ...data,
          // Convert empty strings to null for nullable fields
          labelDe: emptyToNull(data.labelDe),
          descriptionDe: emptyToNull(data.descriptionDe),
        };

        if (isEditing) {
          await updateCategory(data.id, {
            label: categoryData.label,
            labelDe: categoryData.labelDe,
            description: categoryData.description,
            descriptionDe: categoryData.descriptionDe,
            color: categoryData.color,
          });
        } else {
          await createCategory(categoryData);
        }
        form.reset();
        onClose();
      } catch (error) {
        alert(
          `Speichern fehlgeschlagen, bitte erneut versuchen: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    [form, onClose, isEditing]
  );

  //   id: CategoryIdSchema,
  // label: z.string(),
  // labelDe: z.string().optional(),
  // description: z.string(),
  // descriptionDe: z.string().optional(),
  // color: z.nativeEnum(CategoryColor),

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={'Category ID'}
                      disabled={form.formState.isSubmitting || isEditing}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEditing ? 'ID cannot be changed when editing' : 'Unique identifier for the category'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={'Title'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="labelDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Title'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (TXT)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={'Description'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="descriptionDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (TXT)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Description'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={form.formState.isSubmitting}
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {Object.values(CategoryColor).map((color) => (
                        <option key={color} value={color}>
                          {color.charAt(0).toUpperCase() + color.slice(1)}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>Select the category color</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn('py-[8px]')}>
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {form.formState.isSubmitting
                ? 'Saving...'
                : isEditing
                ? 'Update Category'
                : 'Create Category'}
            </button>
          </div>
        </form>
      </Form>
    </>
  );
}
